<script setup>
import { computed, ref } from 'vue';
import TelegramDefaultButtonsDialog from './TelegramDefaultButtonsDialog.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  campaigns: {
    type: Array,
    default: () => [],
  },
  accountId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['close', 'apply']);

const searchQuery = ref('');
const showDefaultButtonsDialog = ref(false);

const closeModal = () => emit('close');
const openDefaultButtonsDialog = () => {
  showDefaultButtonsDialog.value = true;
};
const closeDefaultButtonsDialog = () => {
  showDefaultButtonsDialog.value = false;
};

const normalizeType = type =>
  (type || '').toString().trim().toLowerCase().replaceAll(' ', '_');

const extractPreviewImage = campaign => {
  const telegram = campaign?.template_params?.telegram;
  if (!telegram) return '';
  if (telegram.image_url) return telegram.image_url;

  const blocks = Array.isArray(telegram.blocks) ? telegram.blocks : [];
  const imageBlock = blocks.find(block => {
    const blockType = normalizeType(block?.type);
    return (
      blockType === 'image' ||
      blockType === 'photo' ||
      blockType === 'media_group' ||
      blockType === 'carousel' ||
      blockType === 'paid_media'
    );
  });

  if (!imageBlock) return '';
  const blockType = normalizeType(imageBlock.type);
  if (['image', 'photo'].includes(blockType)) {
    return imageBlock.media_url || imageBlock.image_url || '';
  }
  if (blockType === 'media_group') {
    const item = (Array.isArray(imageBlock.items) ? imageBlock.items : []).find(
      entry => ['image', 'photo'].includes(normalizeType(entry?.type))
    );
    return item?.media_url || item?.media || '';
  }
  if (blockType === 'carousel') {
    const card = (Array.isArray(imageBlock.cards) ? imageBlock.cards : []).find(
      entry =>
        ['image', 'photo'].includes(
          normalizeType(entry?.media_type || entry?.type)
        )
    );
    return card?.media_url || card?.media || '';
  }
  if (blockType === 'paid_media') {
    const item = (Array.isArray(imageBlock.items) ? imageBlock.items : []).find(
      entry => ['image', 'photo'].includes(normalizeType(entry?.type))
    );
    return item?.media_url || item?.media || '';
  }
  return '';
};

const uniqueCampaigns = computed(() => {
  const byId = new Map();
  (Array.isArray(props.campaigns) ? props.campaigns : []).forEach(campaign => {
    if (!campaign?.id) return;
    if (!byId.has(campaign.id)) byId.set(campaign.id, campaign);
  });
  return [...byId.values()];
});

const filteredCampaigns = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return uniqueCampaigns.value;

  return uniqueCampaigns.value.filter(campaign =>
    [campaign?.title, campaign?.message, campaign?.inbox?.name]
      .filter(Boolean)
      .some(text => text.toString().toLowerCase().includes(query))
  );
});

const applyCampaign = campaign => emit('apply', campaign);

const normalizeMessage = message =>
  (message || '')
    .toString()
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
</script>

<template>
  <div v-if="show" class="template-library-overlay">
    <div class="template-library-panel">
      <header class="template-library-header">
        <h2 class="lib-title">Campaign template library</h2>
        <div class="header-actions">
          <button class="secondary-btn" @click="openDefaultButtonsDialog">
            <span class="i-ph-sliders-bold size-4" />
            Default action buttons
          </button>
          <button class="close-btn" @click="closeModal">
            <span class="i-ph-x-bold size-5" />
          </button>
        </div>
      </header>

      <div class="template-library-toolbar">
        <div class="relative max-w-sm w-full">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 text-n-slate-11 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            placeholder="Search campaign templates..."
          />
        </div>
        <div class="text-sm text-n-slate-11">
          {{ filteredCampaigns.length }}
        </div>
      </div>

      <div class="template-library-body">
        <button
          v-for="campaign in filteredCampaigns"
          :key="campaign.id"
          class="template-card"
          type="button"
          @click="applyCampaign(campaign)"
        >
          <div class="template-card__thumb">
            <img
              v-if="extractPreviewImage(campaign)"
              :src="extractPreviewImage(campaign)"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              alt=""
            />
            <span v-else class="i-ph-image-bold size-5 text-n-slate-10" />
          </div>
          <div class="template-card__content">
            <div class="template-card__title">{{ campaign.title }}</div>
            <div class="template-card__message">
              {{ normalizeMessage(campaign.message) || 'No message' }}
            </div>
          </div>
        </button>
      </div>
    </div>

    <TelegramDefaultButtonsDialog
      :show="showDefaultButtonsDialog"
      :account-id="accountId"
      @close="closeDefaultButtonsDialog"
      @saved="closeDefaultButtonsDialog"
    />
  </div>
</template>

<style scoped>
.template-library-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
}

.template-library-panel {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.16),
    0 8px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.lib-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.01em;
}

.template-library-header {
  height: 56px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  background: #ffffff;
  flex-shrink: 0;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  height: 32px;
  border-radius: 8px;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.secondary-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.close-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.template-library-toolbar {
  padding: 12px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f9fafb;
  flex-shrink: 0;
}

.search-input {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  color: #111827;
}

.search-input:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12);
}

.search-input::placeholder {
  color: #9ca3af;
}

.template-library-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  background: #ffffff;
  align-content: start;
}

.template-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  min-height: 170px;
  max-height: 170px;
  transition: all 0.15s ease;
}

.template-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.template-card__thumb {
  height: 96px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.template-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.template-card__content {
  padding: 10px 12px;
  min-height: 0;
  flex: 1;
}

.template-card__title {
  font-weight: 600;
  color: #111827;
  font-size: 13px;
  line-height: 1.3;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-card__message {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
